// Find (⌘K, Console D, APS-322): what a query finds and in what order, apart from how it is
// drawn. Entries come from the reads the console already makes; this ranks them by id and whole
// word, groups them by kind, and counts each kind for the tabs. Prefixes narrow the kind: `/` or
// an HTTP method finds endpoints, `>` actions.

export type EntryKind =
  | 'issue'
  | 'session'
  | 'screen'
  | 'endpoint'
  | 'release'
  | 'page'
  | 'setting'
  | 'action'

/** The tabs, and the order of the groups when every kind is shown. */
export type Group = 'issue' | 'session' | 'perf' | 'release' | 'page' | 'action'
export const GROUPS: readonly Group[] = ['issue', 'session', 'perf', 'release', 'page', 'action']

export const GROUP_OF: Record<EntryKind, Group> = {
  issue: 'issue',
  session: 'session',
  screen: 'perf',
  endpoint: 'perf',
  release: 'release',
  page: 'page',
  setting: 'page',
  action: 'action',
}

export interface Entry {
  /** Unique among the entries. */
  key: string
  kind: EntryKind
  /** What a pasted id matches: a fingerprint, a session id, `POST /v1/orders`, a screen. */
  id: string
  title: string
  sub?: string | undefined
  /** The right-hand figure of the row. */
  meta?: string | undefined
  mono?: boolean | undefined
  /** The figure is a problem (a failing endpoint, a slow screen). */
  bad?: boolean | undefined
  /** More words it is found by. */
  words?: string | undefined
}

export interface Query {
  text: string
  terms: string[]
  /** The kind a prefix asks for. */
  want?: Group | undefined
}

const METHOD = /^(get|post|put|patch|delete|head|options) /i

export function parse(raw: string): Query {
  let text = raw.trim()
  let want: Group | undefined
  if (text.startsWith('>')) {
    want = 'action'
    text = text.slice(1).trim()
  } else if (text.startsWith('/') || METHOD.test(text)) want = 'perf'
  const terms = text.toLowerCase().replace(/…/g, '').split(/\s+/).filter(Boolean)
  return { text, terms, want }
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * How well an entry answers the query: 0 when a term is missing. An exact id first, then an id
 * or title that starts with it, a whole word, a title that contains it.
 */
export function score(e: Entry, q: Query): number {
  const id = e.id.toLowerCase()
  const title = e.title.toLowerCase()
  const hay = `${id} ${title} ${e.sub ?? ''} ${e.meta ?? ''} ${e.words ?? ''}`.toLowerCase()
  if (!q.terms.every((w) => hay.includes(w))) return 0
  const r = q.text.toLowerCase().replace(/…/g, '')
  const word = new RegExp(`(^|[^a-z0-9_])${escapeRe(r)}($|[^a-z0-9_])`).test(`${id} ${title}`)
  return (
    (id === r ? 100 : 0) +
    (id.startsWith(r) ? 50 : 0) +
    (title.startsWith(r) ? 30 : 0) +
    (title.includes(r) ? 15 : 0) +
    (word ? 20 : 0) +
    1 +
    (e.kind === 'page' ? 2 : 0)
  )
}

/** A best match is shown on its own only when it is this strong (an id, or its start). */
const BEST = 50
/** How many of each kind the "all" tab lists. */
const PER_GROUP = 4

export interface Arranged {
  /** The kinds that have matches, each with how many, after "all". */
  tabs: { group: Group | 'all'; count: number }[]
  /** The tab in effect: a prefix's kind, or "all" when the asked one has nothing. */
  tab: Group | 'all'
  sections: { group: Group | 'best'; entries: Entry[] }[]
  /** Every listed entry in order, for the arrows. */
  shown: Entry[]
}

export function arrange(entries: readonly Entry[], q: Query, asked: Group | 'all'): Arranged {
  const scored = entries
    .map((e) => ({ e, s: score(e, q) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
  const count = (g: Group | 'all') =>
    g === 'all' ? scored.length : scored.filter((x) => GROUP_OF[x.e.kind] === g).length
  const tabs = (['all', ...GROUPS] as const)
    .filter((g) => g === 'all' || count(g) > 0)
    .map((g) => ({ group: g, count: count(g) }))
  let tab = asked === 'all' && q.want ? q.want : asked
  if (tab !== 'all' && count(tab) === 0) tab = 'all'
  if (tab !== 'all') {
    const list = scored.filter((x) => GROUP_OF[x.e.kind] === tab).map((x) => x.e)
    return { tabs, tab, sections: list.length ? [{ group: tab, entries: list }] : [], shown: list }
  }
  const top = scored[0] && scored[0].s >= BEST ? scored[0].e : undefined
  const sections: Arranged['sections'] = top ? [{ group: 'best', entries: [top] }] : []
  for (const g of GROUPS) {
    const list = scored
      .filter((x) => GROUP_OF[x.e.kind] === g && x.e !== top)
      .slice(0, PER_GROUP)
      .map((x) => x.e)
    if (list.length) sections.push({ group: g, entries: list })
  }
  return { tabs, tab, sections, shown: sections.flatMap((s) => s.entries) }
}

/** The next tab with Tab (or the one before with Shift Tab), round the ones shown. */
export function nextTab(arranged: Arranged, back = false): Group | 'all' {
  const ks = arranged.tabs.map((t) => t.group)
  if (ks.length === 0) return 'all'
  const i = Math.max(0, ks.indexOf(arranged.tab))
  return ks[(i + (back ? ks.length - 1 : 1)) % ks.length] ?? 'all'
}

/** Where a term matches in a text, for the bold underline: [before, match, after] parts. */
export function highlight(text: string, terms: readonly string[]): { t: string; on: boolean }[] {
  const lower = text.toLowerCase()
  const marks: [number, number][] = []
  for (const term of terms) {
    const at = lower.indexOf(term)
    if (at >= 0) marks.push([at, at + term.length])
  }
  marks.sort((a, b) => a[0] - b[0])
  const parts: { t: string; on: boolean }[] = []
  let i = 0
  for (const [from, to] of marks) {
    if (from < i) continue
    if (from > i) parts.push({ t: text.slice(i, from), on: false })
    parts.push({ t: text.slice(from, to), on: true })
    i = to
  }
  if (i < text.length) parts.push({ t: text.slice(i), on: false })
  return parts
}
